import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { CiteThis } from "@/components/site/CiteThis";
import { GBP_ATTRIBUTES, listAttributeGroups } from "@/lib/reference/attributes";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/tools/gbp-attributes/")({
  head: () => {
    const url = `${SITE.url}/tools/gbp-attributes`;
    return {
      meta: [
        { title: `GBP Attributes Database — Google Business Profile Attributes | ${SITE.name}` },
        {
          name: "description",
          content:
            "Every Google Business Profile attribute explained — what it means, who can set it, and how it surfaces on the Map Pack. Filter by group, search by name.",
        },
        { property: "og:title", content: `GBP Attributes Database | ${SITE.name}` },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Google Business Profile attributes",
            url,
            numberOfItems: GBP_ATTRIBUTES.length,
            itemListElement: GBP_ATTRIBUTES.map((a, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: a.name,
              url: `${url}/${a.slug}`,
            })),
          }),
        },
      ],
    };
  },
  component: AttributesIndex,
});

function AttributesIndex() {
  const [q, setQ] = useState("");
  const [group, setGroup] = useState<string>("All");
  const groups = useMemo(() => ["All", ...listAttributeGroups()], []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return GBP_ATTRIBUTES.filter((a) => {
      if (group !== "All" && a.group !== group) return false;
      if (!needle) return true;
      return a.name.toLowerCase().includes(needle) || a.description.toLowerCase().includes(needle);
    });
  }, [q, group]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-5 sm:px-8 py-16">
        <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
          <ol className="flex items-center gap-2 flex-wrap">
            <li><Link to="/" className="hover:text-accent">Home</Link></li>
            <li aria-hidden>/</li>
            <li><Link to="/tools" className="hover:text-accent">Tools</Link></li>
            <li aria-hidden>/</li>
            <li>GBP Attributes</li>
          </ol>
        </nav>
        <header className="mt-6 max-w-3xl">
          <p className="eyebrow">Reference</p>
          <h1 className="headline-display mt-3 text-5xl">GBP Attributes Database</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Attributes are the structured signals that tell Google — and customers — what your venue offers. Each one has its own page with setter, surface, and category context.
          </p>
        </header>

        <div className="mt-8 flex items-center gap-3 border-t border-b border-border py-4">
          <CiteThis title="GBP Attributes Database" url={`${SITE.url}/tools/gbp-attributes`} />
          <span className="text-xs text-muted-foreground">Free reference — please link back.</span>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search attributes…"
            className="flex-1 min-w-[220px] rounded-sm border border-border bg-paper-soft px-4 py-3 text-sm"
            aria-label="Search attributes"
          />
          <select
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            className="rounded-sm border border-border bg-paper-soft px-4 py-3 text-sm"
            aria-label="Filter by group"
          >
            {groups.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </div>

        <p className="mt-4 text-xs uppercase tracking-wider text-muted-foreground">
          {filtered.length} of {GBP_ATTRIBUTES.length} attributes
        </p>

        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {filtered.map((a) => (
            <li key={a.slug}>
              <Link
                to="/tools/gbp-attributes/$slug"
                params={{ slug: a.slug }}
                className="block rounded-sm border border-border bg-paper-soft p-4 hover:border-foreground hover:bg-background transition-colors"
              >
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{a.group}</p>
                <h2 className="mt-1 text-lg font-semibold">{a.name}</h2>
                <p className="mt-1 text-sm text-foreground/75 line-clamp-2">{a.description}</p>
              </Link>
            </li>
          ))}
        </ul>
      </main>
      <SiteFooter />
    </div>
  );
}
